import {
  formatDate,
  getConceptSubtotal,
  getConceptTotal,
} from "components/Invoices/utils/invoiceUtils"

const normalizeProjectId = projectId => {
  if (typeof projectId !== "string") {
    return projectId ?? null
  }

  const trimmed = projectId.trim()
  return trimmed === "" ? null : trimmed
}

/**
 * Adapta el pedido de compra del estado del formulario al payload esperado por el API.
 * @param {Object} po — estado local del pedido
 * @param {{ isCreate: boolean, purchaseOrderId?: string, purchaseOrderSerie?: string }} options
 * @returns {Object}
 */
export function adaptPurchaseOrderForBackend(
  po,
  {
    isCreate,
    purchaseOrderId: purchaseOrderIdFromRoute,
    purchaseOrderSerie,
  } = { isCreate: false },
) {
  const concepts = Array.isArray(po.purchaseOrderConcepts)
    ? po.purchaseOrderConcepts.map((concept, idx) => {
        const {
          partidaId,
          position,
          description,
          quantity,
          price,
          discount,
          taxes,
          conceptId,
          SK,
        } = concept

        const cleanTaxes = Array.isArray(taxes)
          ? taxes.map(tax => {
              const {
                longName,
                name,
                type,
                value,
                cat,
                rate,
                PK,
                SK: taxSk,
                active,
              } = tax
              const t = { longName, name, type, value, cat, rate }
              t.PK = PK
              t.SK = taxSk
              if (typeof active !== "undefined") t.active = active
              return t
            })
          : []

        const safeQuantity = (() => {
          const q = Number(quantity)
          return Number.isFinite(q) && q >= 1 ? q : 1
        })()
        const safePrice = Number.isFinite(Number(price)) ? Number(price) : 0
        const safeDiscount = Number.isFinite(Number(discount))
          ? Number(discount)
          : 0

        const subtotal = getConceptSubtotal(safeQuantity, safePrice)
        const total = getConceptTotal(
          safeQuantity,
          safePrice,
          cleanTaxes,
          safeDiscount,
        )

        const row = {
          position: typeof position === "number" ? position : idx,
          description: description ?? "",
          quantity: safeQuantity,
          price: safePrice,
          discount: safeDiscount,
          taxes: cleanTaxes,
          subtotal,
          total,
        }
        if (partidaId) row.partidaId = partidaId
        const publicConceptId = conceptId ?? SK
        if (publicConceptId) row.conceptId = publicConceptId
        return row
      })
    : []

  const adapted = {
    projectId: normalizeProjectId(po.projectId),
    contact: po.contact || null,
    issueDate: formatDate(po.issueDate) || null,
    dueDate: po.dueDate ? formatDate(po.dueDate) : null,
    paymentMethod: po.paymentMethod || "cash",
    paymentStatus: po.paymentStatus || "unpaid",
    purchaseOrderConcepts: concepts,
    notes: po.notes ?? null,
    iban: po.iban ?? null,
    expenseId: po.expenseId ?? null,
    subtotal: Number.isFinite(Number(po.subtotal)) ? Number(po.subtotal) : 0,
    discount: Number.isFinite(Number(po.discount)) ? Number(po.discount) : 0,
    tax: Number.isFinite(Number(po.tax)) ? Number(po.tax) : 0,
    retention: Number.isFinite(Number(po.retention)) ? Number(po.retention) : 0,
    total: Number.isFinite(Number(po.total)) ? Number(po.total) : 0,
  }

  if (!isCreate) {
    const id = po.purchaseOrderId ?? purchaseOrderIdFromRoute
    if (id) {
      adapted.purchaseOrderId = String(id)
    }
    if (po.PK) {
      adapted.PK = po.PK
    }
  } else if (purchaseOrderSerie) {
    adapted.purchaseOrderSerie = purchaseOrderSerie
  }

  return adapted
}

export function getPurchaseOrderRowId(row) {
  return (
    row.purchaseOrderId ||
    row.id ||
    (typeof row.SK === "string" ? row.SK.split("#")[1] : row.SK)
  )
}
